import { productConsumerVoiceEndpoint, productConsumerVoiceItemEndpoint } from "../../../../../api/endpoints";
import { CV_OBJECTIVE_SCORES, isCvRunning } from "../../../../../types/consumerVoice";
import type {
  ConsumerVoiceAudit,
  ConsumerVoiceProductReport,
  CvReportMeta,
} from "../../../../../types/consumerVoice";
import { scoredDate } from "../../../videos/selectedVideoCardV2/performance/pageScore";

/**
 * Consumer Voice on the product page — the rules, kept pure.
 *
 * v1 (Karl, 2026-09-28): the section is read-only and shows only the six score
 * cards at the top of a Consumer Voice report. Runs, hints, history, the PDF
 * and the rest of the report stay in the settings modal. The full section is
 * parked at product-details-cv-v2-parked/consumerVoice for v2.
 *
 * The page owns its own calls (the settings modal's code is not edited, D3),
 * so every rule the two surfaces must agree on is COPIED here, verbatim, with
 * the source line beside it. Drift shows up as a diff against those lines.
 * Role checks arrive as booleans: `src/utils` drags @react-pdf in at import
 * time and cannot load in the test runner.
 */

// ---------------------------------------------------------------------------
// Which brand the analysis is for
// ---------------------------------------------------------------------------

export interface CvBrand {
  id: number;
  rmn?: boolean;
}

/**
 * The brand a product's Consumer Voice runs are stamped with — the modal's
 * rule, verbatim (productsV2/productModal/index.tsx:64-65): the first brand
 * that isn't a network, else the first brand, else the scope brand. The page
 * opens the modal with `product` only, so the modal's `brandId` (:51) is
 * `scope.brandId`, and its `productBrands` (:57) is the same `brands || []`.
 * A network-only product therefore reads the network's history, as the modal
 * does. Null = no call at all (the na-network view).
 */
export const cvBrandIdFor = (brands: CvBrand[] | null | undefined, scopeBrandId: number | null | undefined): number | null => {
  const productBrands = (brands as { id: number; name?: string; rmn?: boolean }[]) || [];
  const brandId = scopeBrandId;
  return productBrands.find((b: any) => !b?.rmn)?.id ?? productBrands[0]?.id ?? (brandId || null);
};

/**
 * The brand the PAGE reads Consumer Voice for — where the page deliberately
 * differs from the modal. The modal only calls Consumer Voice when its tab is
 * opened; the page lists runs on every load. For a product carrying more than
 * one regular brand, the modal's rule can pick a brand that isn't the
 * viewer's (product 8360: Moroccanoil 35 first, the brand admin's own 265
 * second), the list answers 403, and the global "You don't have permission
 * to do that." toast fired on every page load.
 *
 * So a NON-internal viewer whose scope brand is a regular (non-network) brand
 * on the product reads their own brand's history. Everyone else — staff in
 * or out of a workspace, a network (RMN) viewer, a viewer whose brand isn't
 * on the product — keeps the modal's rule (`cvBrandIdFor`, unchanged).
 *
 * A network viewer is not sent to Not available up front: the modal's pick
 * is the product's first regular brand, and only the server knows whether
 * that brand is one of the network's suppliers. For _TestRMN 266, 8256 and
 * 8950 carry only suppliers (265, 171) and read fine; 8360's pick,
 * Moroccanoil 35, isn't one, so the list answers 403. That 403 is quiet
 * (`cvListValidateStatus`) and the section says Not available.
 */
export const cvBrandIdForViewer = (
  brands: CvBrand[] | null | undefined,
  scopeBrandId: number | null | undefined,
  isInternalViewer: boolean,
): number | null => {
  if (!isInternalViewer && scopeBrandId) {
    const own = (brands || []).find((b) => Number(b?.id) === Number(scopeBrandId));
    if (own && !own.rmn) return scopeBrandId;
  }
  return cvBrandIdFor(brands, scopeBrandId);
};

/**
 * Whether the settings modal's Consumer Voice tab reads the brand the page
 * reads — the first condition for the page's strips to say "run one from
 * Settings" (`cvSettingsHint`). When they differ (a brand viewer whose own
 * brand is on the product but isn't the modal's pick: 3393 on 8360 reads 265
 * here, the modal 35), a run started there is stamped 35 and never shows
 * here, and the tab's list can answer 403 (the viewer can't run anything
 * there at all).
 */
export const cvSettingsMatchesPage = (
  brands: CvBrand[] | null | undefined,
  scopeBrandId: number | null | undefined,
  isInternalViewer: boolean,
): boolean => {
  const page = cvBrandIdForViewer(brands, scopeBrandId, isInternalViewer);
  const modal = cvBrandIdFor(brands, scopeBrandId);
  if (page == null || modal == null) return page == null && modal == null;
  return Number(page) === Number(modal);
};

/**
 * Whether the settings modal's "Run CV Analysis" button is locked by these
 * rows — the modal's rule, verbatim: ANY pending row, however old, sets its
 * `running` (MODAL/ProductConsumerVoicePanel.tsx:201, `rows.some((r) =>
 * r.status === 'pending')`), which disables Run as "Analysis running…"
 * (:128, :186). No 60-minute window, unlike `isCvRunning`: a pending row the
 * page has given up on (the "stalled" view) still locks it, until the worker
 * or the API's stale sweep moves the row on. When `cvSettingsMatchesPage`,
 * the modal's history reads these same rows (the same endpoint, brandId and
 * limit=20: ProductConsumerVoicePanel.tsx:125, ConsumerVoiceHistorySection.tsx:17,52-53).
 */
export const cvModalRunLocked = (rows: Pick<ConsumerVoiceAudit, "status">[] | null | undefined): boolean =>
  (rows || []).some((r) => r.status === "pending");

/**
 * May a state block point at "this product's Settings → Consumer Voice"?
 * Only when the modal's tab reads the page's brand (`settingsMatches`, from
 * `cvSettingsMatchesPage`) AND its Run button is enabled
 * (`cvModalRunLocked`); otherwise the pointer is a dead end.
 */
export const cvSettingsHint = (
  rows: Pick<ConsumerVoiceAudit, "status">[] | null | undefined,
  settingsMatches: boolean,
): boolean => settingsMatches && !cvModalRunLocked(rows);

// ---------------------------------------------------------------------------
// Which state to draw
// ---------------------------------------------------------------------------

/**
 * loading · na-network / na-access (Not available) · error (the list call
 * failed) · never (no runs) · running (a first run is live) · stalled (none
 * ready or live, but a pending run past the 60-minute window) · failed (runs,
 * none ready, none pending) · ready (the six cards).
 */
export type CvView =
  | "loading"
  | "na-network"
  | "na-access"
  | "error"
  | "never"
  | "running"
  | "stalled"
  | "failed"
  | "ready";

/** How the list call went — the hook's `load`, one state at a time. */
export type CvLoad =
  /** The first list load is out; nothing to show yet. */
  | "loading"
  /** The list landed (its rows are the hook's `rows`). */
  | "loaded"
  /** `cvBrandIdFor` gave null (no call made), or the list answered 404 ProductNotFound (consumerVoice.js:343). */
  | "noBrand"
  /** The list answered 403 (consumerVoice.js:332) — without the global toast (`cvListValidateStatus`). */
  | "noAccess"
  /** Any other list failure. */
  | "failed";

/** The newest ready run's report (the item GET), keyed by the run it belongs to. */
export interface CvItemState {
  id: number | null;
  audit: ConsumerVoiceAudit | null;
  loading: boolean;
  error: boolean;
}

/** A run's date: `scored_at || created_at`, the modal chart's (ConsumerVoiceScoreChart.tsx:18-29). */
const runTime = (r: Pick<ConsumerVoiceAudit, "scored_at" | "created_at">): number =>
  new Date(r.scored_at || r.created_at).getTime();

/** Newest first by `runTime`; equal dates go to the newer id. */
const newestRunFirst = (a: ConsumerVoiceAudit, b: ConsumerVoiceAudit): number =>
  runTime(b) - runTime(a) || b.id - a.id;

/**
 * The newest ready run — the one whose cards the section shows — by its own
 * date (`scored_at || created_at`). The list's own order (`created_at DESC`)
 * is not enough: a delayed run can still finish after a newer one (the
 * 60-minute window only lifts the 409, consumerVoice.js:43,126; the worker
 * still writes it ready, audit.js:600-623).
 */
export const latestReady = (rows: ConsumerVoiceAudit[] | null | undefined): ConsumerVoiceAudit | null =>
  (rows || []).filter((r) => r.status === "ready").sort(newestRunFirst)[0] || null;

/**
 * The Consumer Voice view (first match wins). `rows` is the list, newest first;
 * `ready` is `latestReady(rows)`, which the hook works out once and returns.
 *
 * Any ready run is "ready": a newer run that is pending or failed changes
 * nothing on the page (no banners; the silent poll swaps the cards when a
 * newer run lands ready). With no ready run: no rows is "never"; a run that
 * `isCvRunning` (pending, inside the server's 60-minute stale window) is
 * "running"; a pending run past that window is "stalled"; otherwise every
 * run failed — "failed".
 *
 * "stalled" is not "failed": the page stops polling that run, as the modal's
 * history does (it says Failed), but the worker can still write it ready
 * (the window only lifts the 409, consumerVoice.js:43,126; audit.js:600-623),
 * and the modal's Run stays locked as "Analysis running…" while it is
 * pending (`cvModalRunLocked`). So the page says it may still be finishing,
 * and never points at Settings then. The video page's stalled attempt is the
 * same case (ScoreNowAction.tsx:242-262, "taking longer than expected").
 */
export const cvView = (
  rows: ConsumerVoiceAudit[] | null | undefined,
  load: CvLoad,
  ready: ConsumerVoiceAudit | null,
): CvView => {
  if (load === "loading") return "loading";
  if (load === "noBrand") return "na-network";
  if (load === "noAccess") return "na-access";
  if (load === "failed") return "error";
  // Loaded, but no list for this key yet: still loading, never "No analysis yet".
  if (!rows) return "loading";
  if (ready) return "ready";
  if (rows.length === 0) return "never";
  if (rows.some((r) => isCvRunning(r))) return "running";
  if (cvModalRunLocked(rows)) return "stalled";
  return "failed";
};

// ---------------------------------------------------------------------------
// Reads belong to one product and brand
// ---------------------------------------------------------------------------

/** Which product and brand a read was made for. */
export const cvReadKey = (desireid: number | null | undefined, brandId: number | null | undefined): string =>
  `${desireid ?? ""}:${brandId ?? ""}`;

/** A read, tagged with the `cvReadKey` it was made for. */
export interface CvTagged<T> {
  key: string;
  value: T;
}

/** What the hook shows for the current key. */
export interface CvCurrent {
  /** The list, newest first. Null until this key's first read lands. */
  rows: ConsumerVoiceAudit[] | null;
  /** `latestReady(rows)`: the run whose cards the section shows. */
  ready: ConsumerVoiceAudit | null;
  load: CvLoad;
  /** The ready run's report, only when it was read for that run and this key. */
  item: CvItemState;
}

/**
 * Only what was read for `key`. The reads are state, and the effect that
 * resets them runs after the commit that switches the product or brand, so
 * for that one render they still hold the old key's rows, load and report.
 * A read tagged with another key counts as not there: no rows (so no ready
 * run), the load a first read starts with (`canCall`: the key has a product
 * and a brand; else no call, na-network), no report. The report shows only
 * when it is the ready run's; else it is loading (or empty, with no ready run).
 */
export const cvCurrent = (
  key: string,
  canCall: boolean,
  reads: {
    list: CvTagged<ConsumerVoiceAudit[]> | null;
    load: CvTagged<CvLoad> | null;
    item: CvTagged<CvItemState> | null;
  },
): CvCurrent => {
  const mine = <T>(tagged: CvTagged<T> | null): T | null => (tagged && tagged.key === key ? tagged.value : null);
  const rows = mine(reads.list);
  const ready = latestReady(rows);
  const readyId = ready ? ready.id : null;
  const item = mine(reads.item);
  return {
    rows,
    ready,
    load: mine(reads.load) ?? (canCall ? "loading" : "noBrand"),
    item: item && item.id === readyId ? item : { id: readyId, audit: null, loading: readyId != null, error: false },
  };
};

// ---------------------------------------------------------------------------
// The six score cards
// ---------------------------------------------------------------------------

/**
 * The report's meta — ConsumerVoiceReportView.tsx:684, verbatim: product
 * reports only. Null for a brand report, no report, or no audit (the item call
 * is out or failed).
 */
export const cvReportMeta = (audit: Pick<ConsumerVoiceAudit, "report"> | null | undefined): CvReportMeta | null => {
  const report = audit ? audit.report : null;
  return (report && report.kind === "product" ? (report as ConsumerVoiceProductReport).meta : null) || null;
};

/**
 * "momentum 80 · discussion 49" from a components block, nulls skipped —
 * ConsumerVoiceReportView.tsx:61-68, verbatim (the keys print as stored,
 * underscores to spaces, unless `labels` renames them).
 */
export const componentsLine = (
  block?: { components: Record<string, number | null> } | null,
  labels: Record<string, string> = {},
): string => {
  if (!block || !block.components) return "";
  return Object.keys(block.components)
    .filter((k) => block.components[k] != null)
    .map((k) => `${labels[k] || k.replace(/_/g, " ")} ${block.components[k]}`)
    .join(" · ");
};

export interface CvCard {
  key: "overall_score" | "demand_index" | "opportunity_index" | "trust_score" | "education_score" | "hype_score";
  label: string;
  value: number | null;
  /**
   * The line under the number: the placeholder when there is no value and the
   * report has one, else the caption — IndexCard's rule
   * (ConsumerVoiceReportView.tsx:92).
   */
  text: string;
  /**
   * `text` depends on the report's meta, so it waits for the item call (its
   * `ghosts` as pulse bars meanwhile). Always carries `ghosts`; nothing else does.
   */
  fromMeta: boolean;
  /**
   * With `fromMeta`: every text the line can land as (the longest the meta can
   * make, the defaults and placeholders it falls back to), longest first. The
   * card holds them invisibly in the line's box, so the box is already as
   * tall as the tallest while the report loads and stays so after: the report
   * landing changes the words, never the height below.
   */
  ghosts?: string[];
}

export interface CvCards {
  /** Consumer Voice, Consumer Demand, Content Opportunity (ConsumerVoiceReportView.tsx:715-742). */
  indices: CvCard[];
  /**
   * Trust, Education, Hype (ObjectiveScoreRow, :137-161). Null when the report
   * hides the row: no objective score on the run and no objective blocks in
   * the meta (:139-141).
   */
  objectives: CvCard[] | null;
}

const cardText = (value: number | null, caption: string, placeholder?: string): string =>
  value == null && placeholder ? placeholder : caption;

/**
 * The components each block can list, in the API's order (desirelist-api
 * lib/consumerVoice/scoring.js: demand :165, opportunity :206, trust :256,
 * education :276, hype :292). Demand leaves out `ai_prompt`, a source the API
 * reserves and never fills (:36-37).
 */
const CV_BLOCK_COMPONENTS = {
  demand: ["volume", "momentum", "discussion", "first_party_intent"],
  opportunity: ["demand", "need", "objection", "gap"],
  trust: ["advocacy", "sentiment", "rating", "objection_relief"],
  education: ["understanding", "question_relief", "answered_share", "pdp_clarity"],
  hype: ["momentum", "recency", "discussion", "early_sentiment"],
} as const;

/** The longest line a block can make: every component, each at 100. */
const longestLine = (keys: readonly string[], labels?: Record<string, string>): string =>
  componentsLine(
    { components: keys.reduce<Record<string, number>>((acc, k) => ({ ...acc, [k]: 100 }), {}) },
    labels,
  );

const DEMAND_LABELS = { first_party_intent: "first-party" };
// Each block's `longestLine`, worked out once, for the with-number lines' ghosts.
const DEMAND_LONGEST = longestLine(CV_BLOCK_COMPONENTS.demand, DEMAND_LABELS);
const OPPORTUNITY_LONGEST = longestLine(CV_BLOCK_COMPONENTS.opportunity);
const OBJECTIVE_LONGEST = {
  trust: longestLine(CV_BLOCK_COMPONENTS.trust),
  education: longestLine(CV_BLOCK_COMPONENTS.education),
  hype: longestLine(CV_BLOCK_COMPONENTS.hype),
};
const DEMAND_DEFAULT = "How much consumers search, ask and discuss.";
const DEMAND_NO_SIGNAL = "No search-demand signal came back for this run (Google Trends, Keyword Planner or Search Console).";
const DEMAND_NO_META = "Needs a search-demand source — rerun the analysis to compute it.";
const OPPORTUNITY_DEFAULT = "Where demand and unanswered needs create an opportunity for content.";
const OPPORTUNITY_NEEDS_DEMAND = "Needs a demand signal — computed from demand × unanswered questions × objections × page gaps.";
const OPPORTUNITY_NO_META = "Rerun the analysis to compute it.";

type CvObjective = typeof CV_OBJECTIVE_SCORES[number];
/** "Trust · Build trust & credibility" (ObjectiveScoreRow :152). */
const objectiveLabel = (o: CvObjective): string => `${o.label} · ${o.objective}`;
/** An objective card's line with no number (:155). */
const objectivePlaceholder = (o: CvObjective): string => `Not enough signal for a ${o.label.toLowerCase()} read yet.`;

/**
 * The Objective alignment row as it lands for a run with no objective score
 * whose report has the blocks: three placeholders. `cvObjectivesRow`'s
 * "reserve" holds the room for exactly this, drawn as pulse bars.
 */
export const cvObjectivePlaceholderCards = (): CvCard[] =>
  CV_OBJECTIVE_SCORES.map((o) => ({
    key: o.key,
    label: objectiveLabel(o),
    value: null,
    text: objectivePlaceholder(o),
    fromMeta: false,
  }));

/**
 * A run scored by CV scoring v2, told from its list row: any objective score.
 * v2 always writes the demand and opportunity blocks (scoring.js:395-396), so
 * this run's Demand and Opportunity placeholders are known without the
 * report. (The converse doesn't hold: a v2 run with no signal at all has no
 * objective score — `cvScoredBeforeObjectives`.)
 */
export const cvScoredWithObjectives = (
  run: Pick<ConsumerVoiceAudit, "trust_score" | "education_score" | "hype_score">,
): boolean => CV_OBJECTIVE_SCORES.some((o) => run[o.key] != null);

/**
 * Demand's and Opportunity's placeholder when the report call failed. The
 * report's no-meta placeholders ask for a rerun, which is wrong for a run
 * whose report exists but didn't load (and the page can't rerun).
 */
export const CV_DETAILS_UNAVAILABLE = "Details unavailable.";

/**
 * The six cards, with the report's labels, numbers, captions and placeholders
 * (ConsumerVoiceReportView.tsx:715-744 and ObjectiveScoreRow :137-161). The
 * numbers are the list row's, the same row the report prints. `meta` is null
 * while the report is out or when its call failed: the captions and
 * placeholders are then the report's no-meta ones — except that with
 * `metaFailed` (the item call failed) Demand's and Opportunity's
 * placeholders are CV_DETAILS_UNAVAILABLE instead of "rerun the analysis".
 * A v2 run (`cvScoredWithObjectives`) always has both blocks, so with no
 * number its Demand and Opportunity placeholders are the with-meta ones at
 * once, report or not. Every line that still waits on the report carries its
 * `ghosts`.
 */
export const scoreCardsFor = (
  run: Pick<
    ConsumerVoiceAudit,
    "overall_score" | "subscores" | "demand_index" | "opportunity_index" | "trust_score" | "education_score" | "hype_score"
  >,
  meta: CvReportMeta | null | undefined,
  metaFailed = false,
): CvCards => {
  const sub = run.subscores;
  const demandMeta = meta && meta.demand;
  const oppMeta = meta && meta.opportunity;
  // The report's no-meta placeholders ask for a rerun; not after a failed call.
  const noMeta = (placeholder: string) => (metaFailed ? CV_DETAILS_UNAVAILABLE : placeholder);
  // A v2 run has both blocks, whether or not its report has loaded.
  const v2 = cvScoredWithObjectives(run);

  // :716-725
  const overall = run.overall_score ?? null;
  const voice: CvCard = {
    key: "overall_score",
    label: "Consumer Voice",
    value: overall,
    text: cardText(
      overall,
      sub
        ? `Sentiment ${sub.sentiment ?? "—"} · Advocacy ${sub.advocacy ?? "—"} · Objection severity ${sub.objection_severity ?? "—"}`
        : "How consumers feel: sentiment, advocacy, objection severity.",
      "No usable consumer discussion found, so no score was assigned.",
    ),
    fromMeta: false,
  };

  // :726-735. With no number on a v2 run, the placeholder is known now.
  const demand = run.demand_index ?? null;
  const demandFromMeta = demand != null || !v2;
  const demandCard: CvCard = {
    key: "demand_index",
    label: "Consumer Demand",
    value: demand,
    text: cardText(
      demand,
      componentsLine(demandMeta, DEMAND_LABELS) || DEMAND_DEFAULT,
      demandMeta || v2 ? DEMAND_NO_SIGNAL : noMeta(DEMAND_NO_META),
    ),
    fromMeta: demandFromMeta,
    ...(demandFromMeta && {
      ghosts:
        demand != null
          ? [DEMAND_LONGEST, DEMAND_DEFAULT]
          : [DEMAND_NO_SIGNAL, DEMAND_NO_META, CV_DETAILS_UNAVAILABLE],
    }),
  };

  // :736-741, the same.
  const opportunity = run.opportunity_index ?? null;
  const opportunityFromMeta = opportunity != null || !v2;
  const opportunityCard: CvCard = {
    key: "opportunity_index",
    label: "Content Opportunity",
    value: opportunity,
    text: cardText(
      opportunity,
      componentsLine(oppMeta) || OPPORTUNITY_DEFAULT,
      oppMeta || v2 ? OPPORTUNITY_NEEDS_DEMAND : noMeta(OPPORTUNITY_NO_META),
    ),
    fromMeta: opportunityFromMeta,
    ...(opportunityFromMeta && {
      ghosts:
        opportunity != null
          ? [OPPORTUNITY_DEFAULT, OPPORTUNITY_LONGEST]
          : [OPPORTUNITY_NEEDS_DEMAND, OPPORTUNITY_NO_META, CV_DETAILS_UNAVAILABLE],
    }),
  };

  // ObjectiveScoreRow, :139-157. The row's `anyPresent` (:139) is `v2`.
  const blocks = meta && meta.objective_scores;
  const captionFor = (key: "trust" | "education" | "hype", fallback: string) =>
    blocks && blocks[key] ? componentsLine(blocks[key]) || fallback : fallback;
  const objectives =
    !v2 && !blocks
      ? null
      : CV_OBJECTIVE_SCORES.map((o): CvCard => {
          const value = run[o.key] ?? null;
          const block = o.key.replace("_score", "") as "trust" | "education" | "hype";
          return {
            key: o.key,
            label: objectiveLabel(o),
            value,
            text: cardText(value, captionFor(block, o.caption), objectivePlaceholder(o)),
            // The placeholder doesn't read the meta; the caption does.
            fromMeta: value != null,
            ...(value != null && { ghosts: [OBJECTIVE_LONGEST[block], o.caption] }),
          };
        });

  return { indices: [voice, demandCard, opportunityCard], objectives };
};

/**
 * A ready run scored before CV scoring v2, told from its list row alone: an
 * overall score and no objective score. Scoring v2 writes the three columns
 * and meta.objective_scores together (scoring.js:377-379,397, stored by
 * audit.js:602-611; nothing backfills either), and it can't leave Trust null
 * beside an overall score: Trust is null only when the overall is
 * (scoring.js:262), and an overall needs sentiment and advocacy (`enough`,
 * :338-343), two of Trust's four components, so its weighted mean (:89-102)
 * is a number. So this run's report has no objective blocks, and the row
 * stays hidden.
 *
 * Without an overall score the row can't tell: a v2 run with no usable
 * discussion can have all three null (Trust follows the overall; Education
 * and Hype have their own gates, :282, :298) while its meta still carries the
 * blocks (always written, :397), and the report then shows the row with
 * three placeholders. Nothing else on the row separates the versions
 * (projectAudit, consumerVoice.js:73-97, sends no report or scoring_version),
 * and a date cutoff would guess at the deploy day.
 */
export const cvScoredBeforeObjectives = (
  run: Pick<ConsumerVoiceAudit, "overall_score" | "trust_score" | "education_score" | "hype_score">,
): boolean => run.overall_score != null && !cvScoredWithObjectives(run);

/**
 * The "Objective alignment" row: shown, reserved (a skeleton the row's size)
 * or hidden. Once the report is in, the report's rule (ObjectiveScoreRow
 * :139-141, as `scoreCardsFor`'s `objectives`): shown when the run has any
 * objective score or the meta has objective blocks. With a score on the run it
 * shows at once, whatever the report does. A run scored before v2
 * (`cvScoredBeforeObjectives`) is hidden at once: its report can't have the
 * blocks, so holding a skeleton only made everything below jump up when the
 * report landed (~140px, ~370px at 375). Otherwise (no score of any kind) the
 * answer is in the meta, so the row is reserved while the report loads (the
 * cards below it don't jump when it lands) and hidden when the report call
 * failed. Blocks in a landed meta always win, so the report stays the judge.
 */
export type CvObjectivesRow = "show" | "reserve" | "hide";

export const cvObjectivesRow = (
  run: Pick<ConsumerVoiceAudit, "overall_score" | "trust_score" | "education_score" | "hype_score">,
  meta: CvReportMeta | null | undefined,
  metaLoading: boolean,
  metaFailed = false,
): CvObjectivesRow => {
  if (cvScoredWithObjectives(run)) return "show";
  if (meta && meta.objective_scores) return "show";
  if (cvScoredBeforeObjectives(run)) return "hide";
  if (metaLoading && !metaFailed) return "reserve";
  return "hide";
};

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/** A run's date on the page: "18 Sep 2026", from `scored_at || created_at` (the key `latestReady` sorts on). */
export const runDate = (run: Pick<ConsumerVoiceAudit, "scored_at" | "created_at">): string =>
  scoredDate(run.scored_at || run.created_at);

/**
 * The card's sub-line: "Analysed for Lumière Botanics · 18 Sep 2026". The brand
 * is the one the run is stamped with (`params.brand_name`); never who started it.
 */
export const analysedLine = (run: Pick<ConsumerVoiceAudit, "params" | "scored_at" | "created_at">): string => {
  const brand = run.params?.brand_name?.trim();
  return brand ? `Analysed for ${brand} · ${runDate(run)}` : `Analysed ${runDate(run)}`;
};

// ---------------------------------------------------------------------------
// Calls — the modal's, verbatim
// ---------------------------------------------------------------------------

/** Rows per list read (ConsumerVoiceHistorySection.tsx:17). */
const CV_LIST_LIMIT = 20;

/** The silent list re-read while a run is live (ConsumerVoiceHistorySection.tsx:16). */
export const CV_POLL_MS = 8000;

/**
 * The list GET: the modal's `listEndpoint` (ProductConsumerVoicePanel.tsx:125)
 * plus the history section's `limit` (ConsumerVoiceHistorySection.tsx:52-53).
 * The page only calls it with a brand (no brand = no call).
 */
export const cvListUrl = (desireid: number, brandId: number): string =>
  `${productConsumerVoiceEndpoint(desireid)}?brandId=${brandId}&limit=${CV_LIST_LIMIT}`;

/**
 * The list GET's `validateStatus`: axios's default (2xx resolves) plus 403,
 * which then resolves too. The api interceptor shows its global "You don't
 * have permission to do that." toast only for a rejected 403 (api/index.ts,
 * `notifyForbidden`). A resolved 403 skips it, and the section shows its own
 * Not available. The page lists on every load, so without this a viewer who
 * can't read the brand saw the toast every time (a network viewer on 8360,
 * see `cvBrandIdForViewer`). 401 and every other failure still reject, so the
 * logout redirect and the error strip are unchanged.
 */
export const cvListValidateStatus = (status: number): boolean => (status >= 200 && status < 300) || status === 403;

/** One run with its report (ProductConsumerVoicePanel.tsx:198). */
export const cvItemUrl = (desireid: number, auditId: number, brandId: number | null): string =>
  productConsumerVoiceItemEndpoint(desireid, auditId, brandId);
